import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import type {VendorRequest, VendorResponse} from 'src/api/ProactApiClient';
import {
  useCreateVendorMutation,
  useGetVendorQuery,
  useUpdateVendorMutation,
} from 'src/redux/queries/vendor';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkVendors} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {useGetUserGroupSelectOptionsQuery} from 'src/redux/queries/userGroup';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {
  MapVendorResponseToVendorRequest,
  VendorFormMeta,
} from './vendorHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface VendorFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const VendorForm: React.FC<VendorFormProps> = ({id, mode}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: usergroups, isLoading: isUserGroupsLoading} =
    useGetUserGroupSelectOptionsQuery({
      active: true,
      start: 0,
      length: 10000,
    });

  const {data: fetchedVendor, isLoading: isVendorLoading} = useGetVendorQuery(
    id!,
    {skip: !isEditMode || id === undefined}
  );

  const [createVendor] = useCreateVendorMutation();
  const [updateVendor] = useUpdateVendorMutation();

  const item: VendorResponse | undefined = fetchedVendor;
  const isLoading = isEditMode && isVendorLoading;

  const formMeta = useMemo<IFormFieldV2[]>(
    () => (isUserGroupsLoading ? [] : VendorFormMeta(usergroups?.data ?? [])),
    [isUserGroupsLoading, usergroups]
  );

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedVendor,
    toFormData: toFormDataV2,
  });

  const handleSave = async () => {
    setIsSavePending(true);

    try {
      const values = form.getFieldValues();
      const model: VendorRequest = {
        ...MapVendorResponseToVendorRequest({
          ...values,
          id: item?.id,
        } as VendorResponse),
        targetAutoUserGroupIds: (
          values.autoUserGroupIds as {id: number; label: string}[]
        )?.map(e => e.id),
      };

      let response: VendorResponse;

      if (isCreateMode) {
        response = await createVendor(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
      } else {
        response = await updateVendor(model).unwrap();
        setAlert(
          `${item?.name ?? 'Vendor'} was successfully updated`,
          'success'
        );
      }

      navigate(PathUtility.VendorsView(response.id, 'overview'), {
        replace: true,
      });
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isValid = await form.validateForm();
    if (isValid) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.VendorsView(id, 'overview')
      : PathUtility.Vendors('list')
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode ? 'New Vendor' : item?.name ?? 'Edit Vendor';
  const subtitle = isEditMode ? item?.internalIdentifier : undefined;

  return (
    <Stack gap="lg">
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.VendorsView(id, 'overview')
            : PathUtility.Vendors('list')
        }
        fallbackLabel={isEditMode ? 'Back to Vendor' : 'Back to Vendors'}
        breadcrumbs={[
          {label: 'Vendors', to: PathUtility.Vendors('list')},
          ...(isEditMode && item
            ? [
                {
                  label: item.name ?? 'Vendor',
                  to: PathUtility.VendorsView(id!, 'overview'),
                },
              ]
            : []),
          {label: isCreateMode ? 'New Vendor' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkVendors.icon} subtitle={subtitle}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}

        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create Vendor' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <ProactFormGrid />
      </ProactForm>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Vendor' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
